import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import Tag from "@/components/base/Tag";
import StarRating from "@/components/base/StarRating";
import CharacterCard from "@/components/feature/CharacterCard";
import EpisodeComments from "@/pages/detail/components/EpisodeComments";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const episode = useMemo(() => episodes.find((item) => item.id === id), [id]);

  useDocumentMeta({
    title: episode
      ? `剧集${episode.number} ${episode.title} - 美国式禁忌2剧情介绍与解析`
      : "剧集不存在 - 美国式禁忌2",
    description: episode
      ? `《美国式禁忌2》第${episode.number}集《${episode.title}》剧情介绍与解析：${episode.summary}`
      : "未找到对应的剧集内容，请返回剧集列表查看《美国式禁忌2》全部剧集。",
    keywords: episode
      ? `美国式禁忌2第${episode.number}集,${episode.title},美国式禁忌2剧情解析,${episode.tags.join(",")}`
      : "美国式禁忌2,剧集列表",
  });

  if (!episode) {
    return (
      <SiteLayout>
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <EmptyState
            icon="ri-error-warning-line"
            title="剧集不存在或已删除"
            description="你访问的剧集可能已被移除，或链接有误。可以返回剧集列表重新选择。"
            actionLabel="返回剧集列表"
            actionTo="/category"
          />
        </div>
      </SiteLayout>
    );
  }

  const episodeCharacters = episode.characters
    .map((name) => characters.find((character) => character.name === name))
    .filter(Boolean);

  const prev = episodes.find((item) => item.number === episode.number - 1);
  const next = episodes.find((item) => item.number === episode.number + 1);

  const related = episodes
    .filter(
      (item) => item.id !== episode.id && item.tags.some((tag) => episode.tags.includes(tag))
    )
    .slice(0, 3);

  const metaItems = [
    { icon: "ri-calendar-line", label: "首播日期", value: episode.date },
    { icon: "ri-movie-line", label: "导演", value: episode.director },
    { icon: "ri-quill-pen-line", label: "编剧", value: episode.writer },
    { icon: "ri-time-line", label: "单集时长", value: episode.duration },
    { icon: "ri-map-pin-line", label: "地区", value: episode.region },
    { icon: "ri-star-line", label: "本站评分", value: `${episode.rating.toFixed(1)} / 10` },
  ];

  return (
    <SiteLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-16">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "剧集列表", to: "/category" },
            { label: `第 ${episode.number} 集` },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8">
          <div className="w-full max-w-[320px] mx-auto lg:mx-0">
            <div className="rounded-lg overflow-hidden border border-background-200 bg-background-200">
              <img
                src={episode.cover}
                alt={`美国式禁忌2 第${episode.number}集 ${episode.title} 海报`}
                title={`美国式禁忌2 第${episode.number}集 ${episode.title}`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>
            <div className="mt-4 rounded-lg bg-background-100 border border-background-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground-600">本站综合评分</span>
                <StarRating rating={episode.rating / 2} size="md" showValue={false} />
              </div>
              <p className="mt-2 font-heading text-3xl font-bold text-accent-400">
                {episode.rating.toFixed(1)}
                <span className="text-sm text-foreground-500 font-body font-normal ml-1">
                  / 10
                </span>
              </p>
            </div>
          </div>

          <div>
            <span className="inline-flex items-center h-7 px-3 rounded-full bg-primary-600/90 text-background-50 text-xs font-medium">
              美国式禁忌2 · 第 {episode.number} 集
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {episode.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed border-l-2 border-accent-500 pl-4">
              {episode.tagline}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {episode.tags.map((tag) => (
                <Tag key={tag} variant="accent">
                  {tag}
                </Tag>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {metaItems.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-3 rounded-md bg-background-100 border border-background-200 px-4 py-3"
                >
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-background-200 text-accent-400">
                    <i className={item.icon} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500">{item.label}</span>
                    <span className="block text-sm text-foreground-900 mt-0.5 truncate">
                      {item.value}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <p className="text-xs text-foreground-500 mb-2">主要演员</p>
              <div className="flex flex-wrap gap-2">
                {episode.cast.map((actor) => (
                  <span
                    key={actor}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800"
                  >
                    <i className="ri-user-star-line text-accent-400" />
                    {actor}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
          <div>
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-primary-600" />
                剧情简介与解析
              </h2>
              {episode.synopsis.length > 0 ? (
                <div className="space-y-7">
                  {episode.synopsis.map((section) => (
                    <article key={section.heading}>
                      <h3 className="font-heading text-base md:text-lg font-semibold text-accent-300 mb-2.5">
                        {section.heading}
                      </h3>
                      <div className="space-y-3">
                        {section.body.map((paragraph) => (
                          <p
                            key={paragraph.slice(0, 20)}
                            className="text-sm md:text-[15px] text-foreground-700 leading-[1.9]"
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-600">剧情简介暂无</p>
              )}
            </section>

            {episodeCharacters.length > 0 && (
              <section className="mt-12">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2 mb-5">
                  <span className="w-1.5 h-6 rounded-full bg-primary-600" />
                  本集重要角色
                </h2>
                <p className="text-sm text-foreground-600 mb-4">
                  点击角色卡片，查看该角色相关的剧情与解析内容。
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {episodeCharacters.map((character) =>
                    character ? (
                      <CharacterCard
                        key={character.id}
                        character={character}
                        variant="compact"
                      />
                    ) : null
                  )}
                </div>
              </section>
            )}

            <section className="mt-12">
              <EpisodeComments episodeId={episode.id} />
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-book-open-line text-accent-400" />
                剧情目录
              </h2>
              <div className="flex flex-col">
                {episodes.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className={`flex items-center gap-2 py-2 px-2 rounded-md text-sm cursor-pointer transition-colors ${
                      item.id === episode.id
                        ? "bg-primary-600/15 text-accent-300 font-medium"
                        : "text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
                    }`}
                  >
                    <span className="text-xs text-foreground-500 w-6">
                      {String(item.number).padStart(2, "0")}
                    </span>
                    <span className="truncate">{item.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <i className="ri-compasses-2-line text-accent-400" />
                上下集导航
              </h2>
              <div className="space-y-2">
                {prev ? (
                  <Link
                    to={`/detail/${prev.id}`}
                    className="flex items-center gap-2 text-sm text-foreground-700 hover:text-accent-300 cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-left-line" />
                    上一集 · {prev.title}
                  </Link>
                ) : (
                  <p className="text-sm text-foreground-500">已是第一集</p>
                )}
                {next ? (
                  <Link
                    to={`/detail/${next.id}`}
                    className="flex items-center gap-2 text-sm text-foreground-700 hover:text-accent-300 cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-right-line" />
                    下一集 · {next.title}
                  </Link>
                ) : (
                  <p className="text-sm text-foreground-500">已是最后一集</p>
                )}
              </div>
            </div>

            {related.length > 0 && (
              <div className="rounded-lg bg-background-100 border border-background-200 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-links-line text-accent-400" />
                  相关剧情解析
                </h2>
                <div className="space-y-3">
                  {related.map((item) => (
                    <Link
                      key={item.id}
                      to={`/detail/${item.id}`}
                      className="block group cursor-pointer"
                    >
                      <p className="text-sm text-foreground-800 group-hover:text-accent-300 transition-colors truncate">
                        第 {item.number} 集 · {item.title}
                      </p>
                      <p className="text-xs text-foreground-500 line-clamp-1 mt-0.5">
                        {item.tagline}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}